import Image from 'next/image';
import { useRef } from 'react';
import useComicPlayer from './useComicPlayer.hook';
import { StyledComicPlayer } from './ComicPlayer.styled';
import BubbleTextBox from './BubbleTextBox/BubbleTextBox';
import { Button } from 'antd';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import BubbleExplanation from './BubbleExplanation/BubbleExplanation';
import CartoonBox from './CartoonBox/CartoonBox';
import QuestionInteractions from './Interactions/QuestionInteraction/QuestionInteraction';
import ComicImageAnimations from './ComicAnimations/AnimatedImage/ComicImageAnimations';
import HelpButton from './HelpButton/HelpButton';
import ComicAudioPlayer from './ComicAudioPlayer/ComicAudioPlayer';
import Colors from '@/constants/Colors';
import useIsMobile from '@/hooks/useIsMobile';
import AnimatedStars from '@/shared/StudentPage/StudentPageTopbar/GamificationScoreBoard/AnimatedStars/AnimatedStars';
import { ANCHOR } from '@/modules/dom/getElementCoordinates';
import { ControlBar } from '@/shared/Activities/ActivityComponents/ControlBar/ControlBar';

export default function ComicPlayer({
  pages,
  currentPageId, // For sync with the editor
  initialCue, // For sync with the task progress
  onStart,
  onProgress,
  onRestart,
  progressInterval = 50,
  canMoveForward,
  allowScroll,
  onPageChange,
}) {
  const comicImageRef = useRef();
  const detectedCuesRef = useRef([]);
  const readAudioRef = useRef();
  const isMobile = useIsMobile();
  const { state, actions } = useComicPlayer({
    currentPageId,
    initialCue,
    comicImageRef,
    onStart,
    pages,
    onProgress,
    onPageChange,
    detectedCuesRef,
    readAudioRef,
  });
  const maxPageId = state.maxCueId.split('_')[0];
  const nextPageEnabled =
    state.currentPage?.id < pages.length - 1 &&
    (canMoveForward ||
      state.endOfPageReached ||
      maxPageId > state.currentPage?.id);
  const hideNextPageButton = state.currentPage?.interactions?.some(
    (interaction) => interaction.type == 'question'
  );
  const animations = state.currentPage?.interactions?.filter(
    (i) => i.type == 'animation'
  );

  return (
    state.pageCues && (
      <StyledComicPlayer allowScroll={allowScroll} isMobile={isMobile}>
        <BubbleExplanation
          visible={state.showBubbleExplanation}
          bubble={state.currentBubble}
          onClose={actions.onBubbleExplanationClose}
        />
        <div className="player">
          {state.currentPage && (
            <div
              className="mask-player"
              style={
                state.imgPixelSize && {
                  height: `${state.imgPixelSize.height}px`,
                }
              }
            >
              <div
                className="read-mode-page"
                ref={comicImageRef}
                style={{ opacity: state.readMode ? 1 : 0 }}
              >
                <Image
                  src={state.currentPage?.src}
                  layout={state.imgNaturalSize ? 'responsive' : 'fill'}
                  width={state.imgNaturalSize?.width}
                  height={state.imgNaturalSize?.height}
                  alt=""
                  onLoadingComplete={actions.onPageImageLoaded}
                />
              </div>
              {state.readMode ? null : (
                <div className="play-mode-page">
                  <div className="onion-bg">
                    <Image
                      src={
                        state.currentPage.animationPageBG ??
                        state.currentPage?.src
                      }
                      layout={state.imgNaturalSize ? 'responsive' : 'fill'}
                      width={state.imgNaturalSize?.width}
                      height={state.imgNaturalSize?.height}
                      alt=""
                      //onLoadingComplete={actions.onOnionImageSized}
                    />
                  </div>
                  <div
                    className="masked-boxes"
                    style={
                      state.imgPixelSize && {
                        height: `${state.imgPixelSize.height}px`,
                      }
                    }
                  >
                    {state.currentBox &&
                      state.currentPage?.boxes
                        .filter((box) => box.id <= state.currentBox.id)
                        .map((box) => (
                          <CartoonBox
                            box={box}
                            src={
                              state.currentPage.animationPageBG ??
                              state.currentPage?.src
                            }
                            key={box.id}
                          />
                        ))}
                  </div>
                </div>
              )}
              {state.bubbleCues && state.pageReady && (
                <div
                  className="bubbles-texts"
                  style={
                    state.imgPixelSize && {
                      height: `${state.imgPixelSize.height}px`,
                    }
                  }
                >
                  {state.bubbleCues
                    .filter((cue) => cue.pageId == state.currentPage.id)
                    .map((cue) => {
                      const bubble =
                        pages[cue.pageId].boxes[cue.boxId].bubbles[
                          cue.bubbleId
                        ];
                      const bubbleEnabled =
                        canMoveForward ||
                        state.maxCueId >= cue.id ||
                        state.readMode;

                      const isQuestionBubble =
                        state.currentPage?.interactions?.some((interaction) => {
                          return (
                            interaction.type == 'question' &&
                            interaction.conf.boxIds.includes(cue.boxId)
                          );
                        });
                      return (
                        <div
                          className={`bubble ${bubbleEnabled ? 'enabled' : ''}`}
                          key={cue.id}
                          onClick={() =>
                            bubbleEnabled &&
                            !isQuestionBubble &&
                            actions.onBubblePress(cue)
                          }
                        >
                          <BubbleTextBox
                            readMode={state.readMode}
                            playing={cue.id == state.currentCue?.id}
                            played={cue.id < state.currentCue?.id}
                            bubble={bubble}
                            defaultTextColor={
                              pages[currentPageId]?.defaultTextColor ??
                              Colors.textBlack
                            }
                          />
                        </div>
                      );
                    })}
                </div>
              )}
              {state.currentPage?.interactions?.map((interaction, index) => {
                return {
                  question: (
                    <QuestionInteractions
                      key={index}
                      interaction={interaction}
                      playerState={state}
                      playerActions={actions}
                      detectedCues={detectedCuesRef.current}
                      pages={pages}
                    />
                  ),
                }[interaction.type];
              })}
              {animations?.length > 0 && !state.readMode && (
                <ComicImageAnimations
                  animations={animations}
                  playerState={state}
                  playerActions={actions}
                  detectedCues={detectedCuesRef.current}
                  onAnimationsReady={actions.onAnimationsReady}
                  reset={state.restartAnimations}
                />
              )}
            </div>
          )}
          <ControlBar className="control-bar">
            <div className="left-buttons">
              <audio
                ref={readAudioRef}
                src={state.currentPage?.readAudioSrc}
                preload="auto"
              ></audio>
              <ComicAudioPlayer
                url={
                  state.currentPage.playAudioSrc ??
                  state.currentPage.readAudioSrc
                }
                playing={state.playing && state.playerReady}
                onReady={actions.onPlayerReady}
                onStart={actions.onComicAudioStart}
                onProgress={actions.onPlayProgress}
                onSeek={actions.onSeek}
                onCue={actions.onCue}
                onRestart={onRestart}
                cues={state.pageCues}
                seekTo={state.seekTo}
                progressInterval={progressInterval}
                playPause={actions.playPause}
                pageReady={state.pageReady}
                onEnd={actions.onPageAudioEnd}
              />
              <HelpButton icon={comicPlayerIcons.help} />
            </div>

            <div className="center-buttons">
              <Button
                className="prev-page"
                onClick={actions.prevPage}
                disabled={state.currentPage?.id <= 0}
                type="link"
                size="small"
              >
                <CaretLeftFilled />
              </Button>
              <span className="page-counter">
                {isMobile ? (
                  <>
                    <div className="current-page">
                      {state.currentPage?.id + 1}
                    </div>
                    <div className="divisor">/</div>
                    <div className="page-count"> {pages.length}</div>
                  </>
                ) : (
                  <span>
                    Page {state.currentPage?.id + 1} of {pages.length}
                  </span>
                )}
              </span>
              <Button
                className={`next-page ${
                  state.endOfPageReached && nextPageEnabled ? 'highlighted' : ''
                }
                ${hideNextPageButton ? 'not-visible' : ''}
                `}
                onClick={actions.nextPage}
                disabled={!nextPageEnabled}
                type="link"
                size="small"
              >
                <CaretRightFilled />
              </Button>
            </div>
          </ControlBar>
        </div>
        <AnimatedStars
          originSelector=".mask-player"
          originAnchor={ANCHOR.BOTTOMRIGHT}
        />
      </StyledComicPlayer>
    )
  );
}

const comicAssets =
  process.env.NEXT_PUBLIC_CLOUDFRONT_STATIC_HOST +
  '/commonGameAssets/comic/white/';
const iconPaths = {
  play: comicAssets + 'ICON_PLAY_MENU.svg',
  pause: comicAssets + 'ICON_PAUSE_MENU.svg',
  help: comicAssets + 'ICON_HELP_MENU.svg',
};

export const comicPlayerIcons = Object.fromEntries(
  Object.entries(iconPaths).map(([key, value]) => [
    key,
    <Image key={key} src={value} alt={key} layout="fill" objectFit="contain" />,
  ])
);
